"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { ChevronDown, LogIn01 } from "@untitledui/icons"
import { useState } from "react"

import { batyrTwoFacesSmileBlob as fallbackImage } from "@/assets/images/photos"
import {
  SplitBackgroundEffect,
  SplitImageWrapper,
  SplitQuoteCarousel,
} from "@/components/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link } from "@/components/ui/link"
import { BatyrLogo, BatyrLogoVariant } from "@/components/ui/logo-batyr"
import { AuthSplitLayout } from "@/layouts/auth/auth-split-layout"
import { newIdentityPasswordResetPath, signInPath, signUpPath } from "@/routes"
import type { Brief, DemoUser } from "@/types"

// ----------------------------------------------------------------------------
// Form Pattern: Uncontrolled Inputs + FormData
// ----------------------------------------------------------------------------
// We follow the UUI (Untitled UI) demo pattern: uncontrolled inputs with
// native FormData collection. This makes copy-pasting UUI examples trivial.
//
// Why not Inertia's useForm?
// - useForm requires controlled inputs (value + onChange on every field)
// - React Aria's Input uses onChange(value: string), not onChange(event)
// - UUI demos never use controlled inputs - they use FormData
//
// What we get instead:
// - errors: via usePage().props.errors (inertia_rails: always_include_errors_hash)
// - processing: local useState (simple, explicit)
// - CSRF/redirects: handled by router.post() automatically
// ----------------------------------------------------------------------------

interface PageProps {
  errors: Record<string, string>
  // Dev/staging only; backend omits this key in production. The picker
  // below the form renders iff this is a non-empty array.
  demo_users?: DemoUser[]
  briefs?: Brief[]
  [key: string]: unknown
}

export default function Login() {
  // Errors come from page props - Rails passes them via redirect_to(..., inertia: {errors: ...})
  // or they're included automatically when validation fails (always_include_errors_hash: true)
  const { errors, demo_users, briefs } = usePage<PageProps>().props

  // Simple processing state - no need for useForm's machinery here
  const [processing, setProcessing] = useState(false)
  const [demoExpanded, setDemoExpanded] = useState(false)

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // FormData: the browser-native, UUI-idiomatic way to collect form values.
    // No useState per field. No onChange handlers. Just name attributes.
    router.post(
      signInPath(),
      Object.fromEntries(new FormData(e.currentTarget)),
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  const signInAs = (email: string, password: string) => {
    router.post(
      signInPath(),
      { email, password },
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AuthSplitLayout
      sidebar={
        <SplitQuoteCarousel
          briefs={briefs}
          fallback={<SplitImageWrapper src={fallbackImage} />}
        />
      }
    >
      <SplitBackgroundEffect />
      <Head title="Sign in" />
      <header className="flex flex-col items-center gap-6 text-center">
        <BatyrLogo variant={BatyrLogoVariant.ICON} className="h-12 w-auto" />
        <h1 className="text-display-sm text-primary font-semibold text-balance">
          Welcome back!
        </h1>
      </header>
      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          {/* Uncontrolled input: no value, no onChange. The `name` attribute is all FormData needs. */}
          <Input
            label="Email"
            type="email"
            name="email"
            placeholder="you@example.com"
            size="md"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.email}
            errorMessage={errors?.email}
            autoFocus
            autoComplete="email"
          />
          <Input
            label="Password"
            type="password"
            name="password"
            size="md"
            placeholder="••••••••"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.password}
            errorMessage={errors?.password}
            autoComplete="current-password"
          />
        </div>

        <div className="flex items-center">
          <Link
            href={newIdentityPasswordResetPath()}
            className="text-primary hover:text-secondary ml-auto text-sm font-semibold"
          >
            Forgot password
          </Link>
        </div>

        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            size="lg"
            isDisabled={processing}
            isLoading={processing}
            showTextWhileLoading
            iconLeading={<LogIn01 className="text-navy-500" />}
          >
            Sign in
          </Button>
        </div>
      </form>

      <div className="flex justify-center gap-1 text-center">
        <span className="text-secondary text-sm">New here?</span>
        <Link
          href={signUpPath()}
          className="text-primary hover:text-secondary text-sm font-semibold"
        >
          Sign up
        </Link>
      </div>

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* TEMPORARY: demo accounts picker — dev/staging only. Remove later. */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {demo_users && demo_users.length > 0 && (
        <div className="border-secondary flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-xs">
          <button
            type="button"
            onClick={() => setDemoExpanded((v) => !v)}
            aria-expanded={demoExpanded}
            className="hover:bg-surface-muted -m-1 flex items-center justify-between gap-2 rounded-md p-1 text-left transition-colors"
          >
            <span className="text-tertiary text-xs font-semibold tracking-wide uppercase">
              Demo accounts ({demo_users.length})
            </span>
            <ChevronDown
              aria-hidden="true"
              className={`text-tertiary size-4 transition-transform ${
                demoExpanded ? "rotate-180" : ""
              }`}
            />
          </button>
          {demoExpanded && (
            <>
              <p className="text-tertiary text-center text-xs">
                Available in development and staging only. Click to sign in.
              </p>
              <div className="flex flex-col gap-2">
                {demo_users.map((u) => (
                  <button
                    key={u.email}
                    type="button"
                    onClick={() => signInAs(u.email, u.password)}
                    disabled={processing}
                    className="border-secondary bg-surface-muted hover:bg-brand-50 hover:border-batyr-yellow flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors disabled:opacity-50"
                  >
                    <span className="text-primary text-sm font-semibold">
                      {u.name}
                    </span>
                    <span className="text-tertiary text-xs">{u.email}</span>
                    <span className="text-tertiary text-xs">{u.caption}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {/* ────────────────────────────────────────────────────────────────── */}
      {/* END TEMPORARY: demo accounts picker                                */}
      {/* ────────────────────────────────────────────────────────────────── */}
    </AuthSplitLayout>
  )
}
